import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CurrencyDollarIcon,
  WalletIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface DashboardStats {
  totalTransactions: number;
  totalWallets: number;
  hotWalletBalance: number;
  failedTransactions: number;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalTransactions: 0,
    totalWallets: 0,
    hotWalletBalance: 0,
    failedTransactions: 0,
  });
  const [loading, setLoading] = useState(true);
  const [collectingFunds, setCollectingFunds] = useState(false);
  const [collectMessage, setCollectMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
      const [transactionsRes, walletsRes, hotWalletRes, failedTransactionsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/transaction`),
        axios.get(`${API_BASE_URL}/wallet`),
        axios.get(`${API_BASE_URL}/token/hot-wallet/balance`),
        axios.get(`${API_BASE_URL}/transaction/filter/search?status=failed`),
      ]);

      setStats({
        totalTransactions: transactionsRes.data.meta.total,
        totalWallets: walletsRes.data.meta.total,
        hotWalletBalance: hotWalletRes.data.balance,
        failedTransactions: failedTransactionsRes.data.length,
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCollectFunds = async () => {
    if (!window.confirm('Are you sure you want to collect all funds to the hot wallet? This will transfer all token balances from all wallets.')) {
      return;
    }

    try {
      setCollectingFunds(true);
      setCollectMessage(null);
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
      
      const response = await axios.post(`${API_BASE_URL}/blockchain/collect-funds`);
      
      setCollectMessage(`Success! ${response.data.message}`);
      
      // Refresh dashboard stats after collection
      setTimeout(() => {
        fetchDashboardStats();
      }, 3000);
    } catch (error: any) {
      console.error('Error collecting funds:', error);
      setCollectMessage(`Error: ${error.response?.data?.message || 'Failed to collect funds'}`);
    } finally {
      setCollectingFunds(false);
      // Clear message after 10 seconds
      setTimeout(() => setCollectMessage(null), 10000);
    }
  };

  const stats_cards = [
    {
      name: 'Total Transactions',
      value: stats.totalTransactions,
      icon: CurrencyDollarIcon,
      color: 'bg-blue-500',
    },
    {
      name: 'Total Wallets',
      value: stats.totalWallets,
      icon: WalletIcon,
      color: 'bg-green-500',
    },
    {
      name: 'Hot Wallet Balance',
      value: `${stats.hotWalletBalance.toLocaleString()} BPSK`,
      icon: ChartBarIcon,
      color: 'bg-purple-500',
    },
    {
      name: 'Failed Transactions',
      value: stats.failedTransactions,
      icon: ExclamationTriangleIcon,
      color: 'bg-red-500',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-sm text-gray-700">
          Overview of BPSK token management system
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {stats_cards.map((item) => (
            <div
              key={item.name}
              className="bg-white overflow-hidden shadow rounded-lg"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <item.icon className="h-6 w-6 text-gray-400" aria-hidden="true" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {item.name}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {item.value}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
          Quick Actions
        </h3>
        {collectMessage && (
          <div className={`mb-4 p-4 rounded-md ${collectMessage.startsWith('Success') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            <p className="text-sm">{collectMessage}</p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button 
            onClick={() => navigate('/transactions')}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <CurrencyDollarIcon className="h-4 w-4 mr-2" />
            View Transactions
          </button>
          <button 
            onClick={() => navigate('/wallets')}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <WalletIcon className="h-4 w-4 mr-2" />
            Manage Wallets
          </button>
          <button 
            onClick={() => navigate('/tokens')}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <ChartBarIcon className="h-4 w-4 mr-2" />
            Token Info
          </button>
          <button 
            onClick={handleCollectFunds}
            disabled={collectingFunds}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {collectingFunds ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Collecting...
              </>
            ) : (
              <>
                <WalletIcon className="h-4 w-4 mr-2" />
                Collect Funds
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
